/**
 * src/utils/fbConversions.js
 * ─────────────────────────────────────────────────────────────
 * Fires a Meta Pixel event in the browser AND sends the same event
 * to our Cloud Function (which forwards it server-side to Meta's
 * Conversions API). Both copies share one event_id so Meta
 * deduplicates them into a single event instead of double-counting.
 * ─────────────────────────────────────────────────────────────
 */
const BASE = (process.env.REACT_APP_FUNCTIONS_BASE_URL || '/api').replace(/\/$/, '');

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : undefined;
}

function makeEventId(prefix) {
  try {
    return `${prefix}_${crypto.randomUUID()}`;
  } catch (_) {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
}

/**
 * Manual Advanced Matching — giving the customer's data (email/phone/name/city) to the Pixel
 * so Meta can match the event to the right user. The Pixel SHA-256 hashes these values itself.
 * This solves the Meta Events Manager "Set up manual advanced matching" error.
 */
function buildMatchData(identity = {}) {
  const d = {};
  const email = (identity.email || '').trim().toLowerCase();
  if (email && email !== 'guest' && email.includes('@')) d.em = email;
  const digits = String(identity.phone || '').replace(/\D/g, '');
  if (digits.length >= 10) d.ph = digits.startsWith('92') ? digits : `92${digits.replace(/^0+/, '')}`;
  const name = (identity.name || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (name[0]) d.fn = name[0];
  if (name.length > 1) d.ln = name.slice(1).join(' ');
  const city = (identity.city || '').trim().toLowerCase().replace(/[^a-z]/g, '');
  if (city) d.ct = city;
  if (Object.keys(d).length) d.country = 'pk';
  return d;
}

function applyAdvancedMatching(identity) {
  try {
    const data = buildMatchData(identity);
    if (!window.fbq || !Object.keys(data).length) return;
    window.fbq('init', window.OSF_FB_PIXEL_ID || '1594103588714605', data);
  } catch (_) { /* ignore */ }
}

/**
 * @param {string} eventName   Meta standard event, e.g. 'Purchase', 'AddToCart', 'InitiateCheckout'
 * @param {object} [customData] currency, value, content_ids, content_type, num_items, content_name, content_category...
 *                              (ignored server-side for 'Purchase' — see firestoreOrderId below)
 * @param {object} [identity]   { email, phone } — optional, improves match quality (hashed server-side, never sent to Meta in plain text)
 * @param {string} [eventId]    pass your own to correlate with an order id; otherwise a random one is generated
 * @param {string} [firestoreOrderId] REQUIRED for 'Purchase' — the server looks up the real order
 *                              amount/items from Firestore instead of trusting client-supplied
 *                              numbers, so a forged request can't report a fake Purchase value.
 */
export function trackConversion(eventName, customData = {}, identity = {}, eventId, firestoreOrderId) {
  const id = eventId || makeEventId(eventName.toLowerCase());

  // localhost / IP addresses par (testing) na Pixel na server-side event — Meta data saaf rahe
  if (!window.OSF_TRACKING_ON) return id;

  // 0) Give advanced matching data (if present) to the Pixel BEFORE the event
  applyAdvancedMatching(identity);

  // 1) Browser Pixel (fires immediately, works even if the CAPI call below fails/races)
  try {
    window.fbq && window.fbq('track', eventName, customData, { eventID: id });
  } catch (_) { /* ignore */ }

  // 2) Server-side Conversions API mirror — fire and forget, never blocks the UI
  //    and never surfaces an error to the shopper if Meta/our function is down.
  fetch(`${BASE}/fb/event`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
    body: JSON.stringify({
      eventName,
      eventId: id,
      eventSourceUrl: window.location.href,
      email: identity.email,
      phone: identity.phone,
      name: identity.name,
      city: identity.city,
      fbp: getCookie('_fbp'),
      fbc: getCookie('_fbc'),
      customData,
      firestoreOrderId,
    }),
  }).catch(() => { /* tracking must never break the storefront */ });

  return id;
}

/**
 * Prevents the same event from firing twice within 5 seconds (no duplicates from double click / re-render).
 * Key = event + what it is for (e.g. pack id).
 */
const _recent = {};
export function trackConversionOnce(eventName, key, customData, identity) {
  const k = `${eventName}:${key}`;
  const now = Date.now();
  if (_recent[k] && now - _recent[k] < 5000) return null;
  _recent[k] = now;
  return trackConversion(eventName, customData, identity);
}

/** Builds the standard Meta e-commerce payload from pack details (for both AddToCart / InitiateCheckout). */
export function packEventData(name, price, qty = 1) {
  const value = (parseFloat(price) || 0) * qty;
  return {
    content_name: name,
    content_category: 'Health & Wellness',
    content_ids: ['organic-slim-fit-tea'],
    content_type: 'product',
    contents: [{ id: 'organic-slim-fit-tea', quantity: qty, item_price: parseFloat(price) || 0 }],
    currency: 'PKR',
    value,
    num_items: qty,
  };
}
